import { useThemeColor } from "@/hooks/useThemeColor";
import React, { useEffect } from "react";
import {
  DimensionValue,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from "react-native";
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withTiming,
} from "react-native-reanimated";

type SkeletonVariant = "text" | "circular" | "rectangular" | "rounded";

interface SkeletonProps {
  variant?: SkeletonVariant;
  width?: number | string;
  height?: number;
  style?: StyleProp<ViewStyle>;
  animate?: boolean;
  borderRadius?: number;
}

export default function Skeleton({
  variant = "text",
  width = "100%",
  height,
  style,
  animate = true,
  borderRadius,
}: SkeletonProps) {
  const backgroundColor = useThemeColor({}, "background");
  const isDark = backgroundColor === "#000000" || backgroundColor === "#151718";

  const opacity = useSharedValue(1);

  useEffect(() => {
    if (animate) {
      opacity.value = withRepeat(
        withTiming(0.5, {
          duration: 1000,
          easing: Easing.inOut(Easing.ease),
        }),
        -1,
        true
      );
    }
  }, [animate, opacity]);

  const animatedStyle = useAnimatedStyle(() => ({
    opacity: opacity.value,
  }));

  const getDefaultDimensions = () => {
    const parseWidth = (w: number | string | undefined): DimensionValue => {
      if (typeof w === "number") return w;
      if (typeof w === "string" && w.trim().endsWith("%"))
        return w as DimensionValue;
      if (typeof w === "string" && !isNaN(Number(w))) return Number(w);
      return "100%";
    };

    switch (variant) {
      case "text":
        return {
          width: parseWidth(width),
          height: height || 16,
          borderRadius: 4,
        };
      case "circular":
        const size = height || 40;
        return { width: size, height: size, borderRadius: size / 2 };
      case "rounded":
        return {
          width: parseWidth(width),
          height: height || 40,
          borderRadius: 12,
        };
      case "rectangular":
      default:
        return {
          width: parseWidth(width),
          height: height || 100,
          borderRadius: 0,
        };
    }
  };

  const dimensions = getDefaultDimensions();

  const skeletonBgColor = isDark ? "#2A2A2A" : "#E0E0E0";

  return (
    <View style={[styles.container, style]}>
      <Animated.View
        style={[
          {
            backgroundColor: skeletonBgColor,
            width: dimensions.width,
            height: dimensions.height,
            borderRadius:
              borderRadius !== undefined
                ? borderRadius
                : dimensions.borderRadius,
          },
          animate && animatedStyle,
        ]}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    overflow: "hidden",
  },
  card: {
    borderRadius: 16,
    overflow: "hidden",
  },
  cardContent: {
    padding: 16,
  },
  listItem: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 12,
  },
  listItemContent: {
    flex: 1,
  },
  profile: {
    alignItems: "center",
    padding: 20,
  },
  profileStats: {
    flexDirection: "row",
    gap: 24,
  },
  statItem: {
    alignItems: "center",
  },
});

// Pre-built skeleton layouts for common use cases
interface SkeletonCardProps {
  style?: StyleProp<ViewStyle>;
}

export function SkeletonCard({ style }: SkeletonCardProps) {
  return (
    <View style={[styles.card, style]}>
      <Skeleton variant="rectangular" height={200} />
      <View style={styles.cardContent}>
        <Skeleton
          variant="text"
          height={24}
          width="80%"
          style={{ marginBottom: 8 }}
        />
        <Skeleton
          variant="text"
          height={16}
          width="100%"
          style={{ marginBottom: 4 }}
        />
        <Skeleton variant="text" height={16} width="90%" />
      </View>
    </View>
  );
}

interface SkeletonListItemProps {
  hasAvatar?: boolean;
  style?: StyleProp<ViewStyle>;
}

export function SkeletonListItem({
  hasAvatar = false,
  style,
}: SkeletonListItemProps) {
  return (
    <View style={[styles.listItem, style]}>
      {hasAvatar && (
        <Skeleton variant="circular" height={48} style={{ marginRight: 12 }} />
      )}
      <View style={styles.listItemContent}>
        <Skeleton
          variant="text"
          height={18}
          width="70%"
          style={{ marginBottom: 8 }}
        />
        <Skeleton variant="text" height={14} width="50%" />
      </View>
    </View>
  );
}

interface SkeletonProfileProps {
  style?: StyleProp<ViewStyle>;
}

export function SkeletonProfile({ style }: SkeletonProfileProps) {
  return (
    <View style={[styles.profile, style]}>
      <Skeleton variant="circular" height={80} style={{ marginBottom: 16 }} />
      <Skeleton
        variant="text"
        height={24}
        width={150}
        style={{ marginBottom: 8 }}
      />
      <Skeleton
        variant="text"
        height={16}
        width={200}
        style={{ marginBottom: 16 }}
      />
      <View style={styles.profileStats}>
        <View style={styles.statItem}>
          <Skeleton
            variant="text"
            height={20}
            width={60}
            style={{ marginBottom: 4 }}
          />
          <Skeleton variant="text" height={14} width={80} />
        </View>
        <View style={styles.statItem}>
          <Skeleton
            variant="text"
            height={20}
            width={60}
            style={{ marginBottom: 4 }}
          />
          <Skeleton variant="text" height={14} width={80} />
        </View>
        <View style={styles.statItem}>
          <Skeleton
            variant="text"
            height={20}
            width={60}
            style={{ marginBottom: 4 }}
          />
          <Skeleton variant="text" height={14} width={80} />
        </View>
      </View>
    </View>
  );
}
